import {
	PageModule,
	Session,
	SessionDisplayAll,
	SessionDisplayOptions,
	SessionPreview,
	SessionTypesEnum,
} from '../../../../types/working-model';

export const noTrackName = 'bl_untracked';

export const scheduledSessionTypes = [SessionTypesEnum.broadcast, SessionTypesEnum.fireside, SessionTypesEnum.breakoutRooms];

export const getLiveSessionsSelectedForDisplay = (sessions: Array<Session | SessionPreview>, module: PageModule, allTrackOverride = false) => {
	if (allTrackOverride) return sessions;
	if (!module.module_display_info || !module.module_display_info.session_display_options) return sessions;
	if (module.module_display_info.session_display_all?.liveSessions) return sessions;

	const sessionDisplayOptions = module.module_display_info.session_display_options;
	const allDisplayedSessions = Object.values(sessionDisplayOptions).flat();
	const sessionsDisplayed = sessions.filter(({ session: sessionId }) => allDisplayedSessions.includes(sessionId));

	return sessionsDisplayed;
};

const deepCopy = <T>(object: T): T => JSON.parse(JSON.stringify(object));

const addToTrack = (track: string, session: (Session | SessionPreview), sessionsByTrack: { [key: string]: (Session | SessionPreview | undefined)[] }) => {
	if (!sessionsByTrack[track]) sessionsByTrack[track] = [];
	sessionsByTrack[track].push(session);
};


/**
 * Uses the select all to provide a fully flushed out version of
 * session display options.  This allows for not having to check
 * the display all flags, and simply iterate over the sessionDisplayOptions object
 * @param sessionDisplayOptions 
 * @param sessionDisplayAll
 * @param sessions 
 * @param allTrackOverride Always returns all sessions, regardless of on demand enablement
 */
export const fillOutSessionDisplayOptions = (
	sessionDisplayOptions: SessionDisplayOptions,
	sessionDisplayAll: SessionDisplayAll,
	sessions: (Session | SessionPreview)[],
	allTrackOverride = false,
): SessionDisplayOptions => {
	// Must deep copy as the object contains array refs
	const newSessionDisplayOptions: SessionDisplayOptions = deepCopy<SessionDisplayOptions>(sessionDisplayOptions);

	// ensure displayOptions are up to date
	Object.values(sessions).forEach(session => {
		const { tracks } = session;
		const { session: sessionId } = session;

		// add any untracked
		// Pushing onto the stack too allow re-use of track loop
		const workingTracks = [...deepCopy(tracks || [])];
		if (tracks?.length === 0) workingTracks.push(noTrackName);

		// Add to named tracks
		workingTracks?.forEach(track => {
			// if select all is enabled, add any tracks that aren't already present.
			// This is done as a follow on to respect the order that was established
			if (
				(session.session_type === SessionTypesEnum.onDemand && sessionDisplayAll.onDemandSessions)
				|| (scheduledSessionTypes.includes(session.session_type) && sessionDisplayAll.liveSessions)
				// Only applies to on-demand because live is 'special'.  This will allow
				// an item to show when it transitions to on-demand.
				|| (sessionDisplayAll[track] && session.session_type === SessionTypesEnum.onDemand)
				|| allTrackOverride // agenda should show everything
			) {
				if (!newSessionDisplayOptions[track]) newSessionDisplayOptions[track] = [];
				if (!newSessionDisplayOptions[track].includes(sessionId)) {
					newSessionDisplayOptions[track].push(sessionId);
				}
			}
		});
	});

	// Remove sessions from tracks they are no longer present in
	Object.keys(newSessionDisplayOptions).forEach(track => {
		const sessionIds = newSessionDisplayOptions[track]
			.reduce((acc: number[], sessionId) => {
				const session = sessions.find(sess =>
					sess.session === sessionId
					&& (sess.tracks?.includes(track)
						|| (track === noTrackName
							&& (!sess.tracks || sess.tracks?.length === 0))
					)
				);
				if (session) { acc.push(sessionId); }
				return acc;
			}, []);
		newSessionDisplayOptions[track] = sessionIds;
	});

	return newSessionDisplayOptions;
};

export const getOnDemandSessionsSelectedForDisplay = (
	sessionDisplayOptions: SessionDisplayOptions,
	sessionDisplayAll: SessionDisplayAll,
	sessions: (Session | SessionPreview)[],
	returnAllSessions: boolean,
) => {
	const ondemandTracks: { [key: string]: (Session | SessionPreview)[] } = {};

	// Initial tracks by order and selected status
	Object.keys(sessionDisplayOptions).forEach(track => {
		const sessionsArray = sessionDisplayOptions[track]
			.reduce((acc: (Session | SessionPreview)[], sessionId) => {
				const session = sessions.find(sess =>
					sess.session === sessionId
					&& sess.session_type === SessionTypesEnum.onDemand
					&& (sess.tracks?.includes(track)
						|| (track === noTrackName
							&& (!sess.tracks || sess.tracks?.length === 0))
					)
				);
				if (session) { acc.push(session); }
				return acc;
			}, []);
		if (sessionsArray.length > 0) ondemandTracks[track] = sessionsArray;
	});

	// if select all is enabled, add any tracks that aren't already present.
	// This is done as a follow on to respect the order that was established
	Object.values(sessions).forEach(session => {
		if (session.session_type === SessionTypesEnum.onDemand) {
			const { tracks } = session;
			if (tracks?.length === 0
				&& (sessionDisplayAll.onDemandSessions
					|| sessionDisplayAll[noTrackName]
					|| returnAllSessions)
			) {
				addToTrack(noTrackName, session, ondemandTracks);
			}

			tracks?.forEach(track => {
				if (sessionDisplayAll.onDemandSessions
					|| sessionDisplayAll[track]
					|| returnAllSessions
				) {
					addToTrack(track, session, ondemandTracks);
				}
			});
		}
	});

	// Ensure no duplicates
	Object.keys(ondemandTracks).forEach(track => {
		ondemandTracks[track] = [...new Set(ondemandTracks[track])];
	});

	return ondemandTracks;
};

export const createDefaultTracksContent = (sessions: (Session | SessionPreview)[] | null | undefined) => {
	// CREATES THIS:
	// {
	// 	"track_1":[5492,6757,1234],
	// 	"bl_untracked": [7001,7002]
	// }

	if (!sessions) return {};
	const defaultGroup: number[] = [];
	const ondemandTracksContent: { [key: string]: (number)[] } = {};

	sessions.map(session => {
		if (!session) return;
		if (session.tracks?.length === 0 && session.session_type !== SessionTypesEnum.broadcast) {
			defaultGroup.push(session.session);
			return;
		}

		session.tracks?.map(track => {
			if (!(track in ondemandTracksContent)) {
				ondemandTracksContent[track] = [session.session];
			}
			else {
				ondemandTracksContent[track].push(session.session);
			}
		});
	});

	ondemandTracksContent['bl_untracked'] = defaultGroup;

	return (ondemandTracksContent);

};

export const getDisplayedTracksInSpecifiedOrder = (sessions: (Session | SessionPreview)[], currentTrackOrder: string[]) => {
	const allTracks = getAllTrackNames(sessions);
	const combinedSelectedAndAll = [...currentTrackOrder, ...allTracks];
	const uniqueTracks = [...new Set(combinedSelectedAndAll)];
	return uniqueTracks;
};

export const getAllTrackNames = (sessions: (Session | SessionPreview)[]) => {
	const tracks: string[] = sessions.reduce((acc: string[], session: (Session | SessionPreview)) => {
		session.tracks?.forEach(track => acc.push(track));
		return acc;
	}, []);

	const uniqueTracks = [...new Set(tracks)];
	const sortedTracks = uniqueTracks.sort();
	sortedTracks.push(noTrackName);

	return sortedTracks;
};
